import { useEffect, useRef, useState } from "react";
import SVG from "../../../Components/SVG";

const getOptionValue = (option) => String(option?.value ?? option ?? "");
const getOptionLabel = (option) => option?.label ?? option;

export default function InputSelect({
  options = [],
  defaultValue,
  id,
  name,
  value,
  onChange,
}) {
  const [internalValue, setInternalValue] = useState(() =>
    getOptionValue(value ?? defaultValue ?? options[0]),
  );
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedValue = getOptionValue(value ?? internalValue);
  const selectedOption =
    options.find((option) => getOptionValue(option) === selectedValue) ??
    options[0] ??
    null;

  const handleSelect = (nextValue) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      {name && <input type="hidden" name={name} value={selectedValue} />}

      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={id ? `${id}-options` : undefined}
        onClick={() => setIsOpen((current) => !current)}
        className="w-full rounded-lg py-1.5 px-2.5 flex items-center justify-between gap-2.5 bg-zi-bg1-light dark:bg-zi-inputbg-dark text-sm text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-dark dark:hocus:ring-0"
      >
        <span>{selectedOption ? getOptionLabel(selectedOption) : ""}</span>
        <SVG
          name="select"
          classname={`size-3 shrink-0 stroke-zi-text-light dark:stroke-zi-text-dark transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          id={id ? `${id}-options` : undefined}
          role="listbox"
          aria-labelledby={id}
          className="absolute inset-x-0 mt-2 z-50 max-h-64 overflow-auto rounded-[10px] border border-zi-border-light/60 dark:border-zi-border-light/10 bg-zi-bg2-light dark:bg-zi-bg2-dark shadow-lg"
        >
          {options.map((option, index) => {
            const optionValue = getOptionValue(option);
            const isActive = optionValue === selectedValue;

            return (
              <button
                key={`${optionValue}-${index}`}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(optionValue)}
                className={`w-full px-3 py-2 flex items-center justify-between gap-2.5 text-sm text-zi-text-light dark:text-zi-text-dark hocus:bg-zi-bg1-light/50 dark:hocus:bg-white/[0.03] ${
                  isActive ? "bg-zi-bg1-light/50 dark:!bg-neutral-800" : ""
                }`}
              >
                <span>{getOptionLabel(option)}</span>
                {isActive && (
                  <SVG
                    name="check"
                    classname="size-4 shrink-0 stroke-zi-text-light dark:stroke-zi-text-dark"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
